/**
 * DJ workflow — compatible-track suggestions.
 *
 * Given the current analysis (key + BPM), pulls the analysis history,
 * scores each candidate via Camelot key compatibility + tempo proximity,
 * and surfaces the top matches as cards a DJ can use to plan the next
 * track in the set.
 */

import { useEffect, useMemo, useState } from 'react';
import { Disc3, Wand2 } from 'lucide-react';
import {
  AnalysisHistoryItem,
  getHistory,
} from '../lib/api';
import {
  mixCompatibility,
  toCamelot,
  type MixCompatibility,
} from '../utils/camelot';
import './DJSuggestions.css';

interface DJSuggestionsProps {
  currentAnalysisId: string;
  currentKey?: string;
  currentBpm?: number;
}

interface ScoredTrack extends AnalysisHistoryItem {
  compat: MixCompatibility;
}

export function DJSuggestions({
  currentAnalysisId,
  currentKey,
  currentBpm,
}: DJSuggestionsProps) {
  const [history, setHistory] = useState<AnalysisHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getHistory()
      .then((data) => {
        if (!cancelled) {
          setHistory(data || []);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const suggestions: ScoredTrack[] = useMemo(() => {
    if (!currentKey && !currentBpm) return [];
    const others = history.filter(
      (h) => h.analysisId !== currentAnalysisId && h.status === 'completed',
    );
    const scored: ScoredTrack[] = others.map((track) => ({
      ...track,
      compat: mixCompatibility({
        keyA: currentKey,
        keyB: track.key,
        bpmA: currentBpm,
        bpmB: track.tempo,
      }),
    }));
    scored.sort((a, b) => b.compat.score - a.compat.score);
    return scored.filter((s) => s.compat.score > 0.3).slice(0, 6);
  }, [history, currentAnalysisId, currentKey, currentBpm]);

  const ownCamelot = toCamelot(currentKey);

  if (loading) {
    return (
      <div className="dj-suggestions dj-suggestions-loading">
        <div className="dj-suggestions-header">
          <Wand2 size={16} strokeWidth={1.8} />
          <h2>Mix Suggestions</h2>
        </div>
        <p className="dj-empty">Loading your library…</p>
      </div>
    );
  }

  if (suggestions.length === 0) {
    return (
      <div className="dj-suggestions">
        <div className="dj-suggestions-header">
          <Wand2 size={16} strokeWidth={1.8} />
          <h2>Mix Suggestions</h2>
        </div>
        <p className="dj-empty">
          No compatible tracks in your library yet. Upload + analyze a few
          more tracks at similar tempos to start getting harmonic-mix
          recommendations.
        </p>
      </div>
    );
  }

  return (
    <div className="dj-suggestions">
      <div className="dj-suggestions-header">
        <Wand2 size={16} strokeWidth={1.8} />
        <h2>Mix Suggestions</h2>
        {ownCamelot && (
          <span className="dj-self-camelot" title="This track on the Camelot wheel">
            {ownCamelot}
          </span>
        )}
      </div>
      <p className="dj-suggestions-sub">
        Tracks that should mix harmonically — scored by Camelot key compatibility
        and tempo proximity to this track.
      </p>
      <div className="dj-suggestions-list">
        {suggestions.map((track) => (
          <SuggestionCard key={track.analysisId} track={track} />
        ))}
      </div>
    </div>
  );
}

function SuggestionCard({ track }: { track: ScoredTrack }) {
  const { compat } = track;
  const scorePct = Math.round(compat.score * 100);
  const tempoDelta =
    typeof track.tempo === 'number'
      ? `${track.tempo.toFixed(0)} BPM`
      : '—';
  const camelot = compat.camelotB || '—';

  // Compatibility tag — friendlier than a raw decimal.
  let tag: { label: string; tone: string } = { label: 'Possible blend', tone: 'soft' };
  if (compat.score >= 0.9) tag = { label: 'Perfect match', tone: 'live' };
  else if (compat.score >= 0.75) tag = { label: 'Strong neighbour', tone: 'hero' };
  else if (compat.score >= 0.5) tag = { label: 'Energy shift', tone: 'budget' };

  return (
    <a
      className="dj-card"
      href={`/results/${track.analysisId}`}
      data-tone={tag.tone}
    >
      <div className="dj-card-header">
        <Disc3 size={14} strokeWidth={1.8} />
        <span className="dj-card-camelot">{camelot}</span>
        <span className={`dj-card-tag dj-card-tag-${tag.tone}`}>{tag.label}</span>
      </div>
      <div className="dj-card-title" title={track.filename}>
        {track.filename || track.analysisId}
      </div>
      <div className="dj-card-meta">
        <span>{tempoDelta}</span>
        <span className="dj-card-key">{track.key || 'Key —'}</span>
      </div>
      <div className="dj-card-score">
        <div className="dj-card-bar" style={{ width: `${scorePct}%` }} />
        <span className="dj-card-score-text">{scorePct}% compatible</span>
      </div>
    </a>
  );
}
